// ./src/components/features/Policies/PolicyTextEditor/PolicyTextEditor.tsx
//
// The policy-body editor. Picks the right editor for the chosen text format:
//   • plain    → a plain textarea
//   • markdown → MarkdownField (CodeMirror source + live preview)
//   • html     → RichTextField (Tiptap WYSIWYG)
// The format selector sits ABOVE the text (it decides which editor renders), and
// a full-screen mode gives room for long policies. Fully controlled.
//
// ⚠ NOT POLICY-ONLY ANY MORE (2026-09-18): the partner offering's "What applicants see" reuses it,
// because formats + full screen + a sanitizing public renderer (`renderPolicyHtml`) already exist
// here as one set. The only policy-specific part is the merge-variable menu, which is now a prop —
// it defaults to the policy variables so every existing caller is unchanged, and a caller whose text
// has no variables passes `[]` so no menu offers tokens its renderer would print as blanks.

import React, { useState, useCallback, Suspense } from "react";
import { Stack, Group, Text, Textarea, ActionIcon, Tooltip, Box, Input, Skeleton } from "@mantine/core";
import { StackedModal } from "@em/ui";
import { useViewportSize } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { POLICY_CONTENT_FORMAT_VALUES } from "@/utils/api";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";
import { AppIcon, SelectField } from "@/components/generic";

import { VariableMenuItem } from "@/components/generic/VariableMenu/VariableMenu";

import { POLICY_VARIABLES } from "../policyVariables";

// Heavy editors (Tiptap / CodeMirror) — lazy so they load only when a rich
// format is actually edited, and never bloat the shared bundle.
const RichTextField = React.lazy(() =>
  import("@/components/generic/RichTextField/RichTextField").then((m) => ({ default: m.RichTextField }))
);
const MarkdownField = React.lazy(() =>
  import("@/components/generic/MarkdownField/MarkdownField").then((m) => ({ default: m.MarkdownField }))
);
const HtmlField = React.lazy(() =>
  import("@/components/generic/HtmlField/HtmlField").then((m) => ({ default: m.HtmlField }))
);

export interface PolicyTextEditorProps {
  label?: string;
  description?: string;
  format: string;
  onFormatChange: (format: string) => void;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: React.ReactNode;
  /** Merge variables offered while editing. Defaults to the policy variables; `[]` for none. */
  variables?: VariableMenuItem[];
  /**
   * Told when full screen opens and closes. ⚠ A caller inside a DRAWER needs it: Mantine's modals each listen for Esc on
   * the window and none stops it, so one Esc that closes full screen ALSO closes the drawer underneath and throws away
   * what was typed. The drawer turns its own Esc off while this reports `true` (the Schedule drawer does).
   */
  onFullscreenChange?: (fullscreen: boolean) => void;
}

const INLINE_MIN_HEIGHT = 280;

export const PolicyTextEditor: React.FC<PolicyTextEditorProps> = ({
  label,
  description,
  format,
  onFormatChange,
  value,
  onChange,
  placeholder,
  error,
  variables = POLICY_VARIABLES,
  onFullscreenChange,
}) => {
  const { t } = useTranslation(["policies", "common"]);
  const { height } = useViewportSize();
  const [fullscreen, setFullscreen] = useState(false);

  const open = useCallback(() => {
    setFullscreen(true);
    onFullscreenChange?.(true);
  }, [onFullscreenChange]);
  const close = useCallback(() => {
    setFullscreen(false);
    onFullscreenChange?.(false);
  }, [onFullscreenChange]);

  const formatOptions = POLICY_CONTENT_FORMAT_VALUES.map((f) => ({
    value: f,
    label: t(`policies:formats.${f}`, { defaultValue: f }),
  }));

  const handleFormat = useCallback(
    (v: string | null) => onFormatChange(v || "markdown"),
    [onFormatChange]
  );

  // The editor for the current format, sized for inline vs full-screen.
  const renderEditor = (isFullscreen: boolean) => {
    const minHeight = isFullscreen ? Math.max(400, height - 220) : INLINE_MIN_HEIGHT;

    if (format === "richtext") {
      return (
        <Suspense fallback={<Skeleton height={minHeight} radius="sm" />}>
          <RichTextField
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            minHeight={minHeight}
            variables={variables}
          />
        </Suspense>
      );
    }

    if (format === "markdown") {
      return (
        <Suspense fallback={<Skeleton height={minHeight} radius="sm" />}>
          <MarkdownField
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            minHeight={minHeight}
            variables={variables}
          />
        </Suspense>
      );
    }

    if (format === "html") {
      return (
        <Suspense fallback={<Skeleton height={minHeight} radius="sm" />}>
          <HtmlField
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            minHeight={minHeight}
            variables={variables}
          />
        </Suspense>
      );
    }

    return (
      <Textarea
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        placeholder={placeholder}
        autosize={!isFullscreen}
        minRows={isFullscreen ? undefined : 8}
        maxRows={isFullscreen ? undefined : 16}
        styles={isFullscreen ? { input: { minHeight } } : undefined}
      />
    );
  };

  const formatSelect = (size: "xs" | "sm") => (
    <SelectField
      aria-label={t("policies:form.fields.contentFormat.label", { defaultValue: "Text format" })}
      data={formatOptions}
      value={format}
      onChange={(_name, v) => handleFormat(v)}
      allowDeselect={false}
      comboboxProps={DRAWER_COMBOBOX_PROPS}
      size={size}
      w={150}
    />
  );

  return (
    <>
      <Stack gap={6}>
        <Group justify="space-between" align="flex-end" wrap="nowrap">
          <div style={{ minWidth: 0 }}>
            {label && (
              <Text component="label" fw={500} size="md">
                {label}
              </Text>
            )}
          </div>
          <Group gap="xs" wrap="nowrap">
            {formatSelect("xs")}
            <Tooltip label={t("policies:form.text.fullscreen", { defaultValue: "Full screen" })} withArrow>
              <ActionIcon variant="light" size="lg" onClick={open} aria-label={t("policies:form.text.fullscreen", { defaultValue: "Full screen" })}>
                <AppIcon iconName="Maximize" size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        {description && (
          <Text size="xs" c="dimmed">
            {description}
          </Text>
        )}

        {/* Hidden while full-screen so only one editor instance is mounted. */}
        {!fullscreen && renderEditor(false)}

        {error && <Input.Error>{error}</Input.Error>}
      </Stack>

      <StackedModal
        opened={fullscreen}
        onClose={close}
        fullScreen
        withCloseButton={false}
        padding="md"
        title={
          <Group justify="space-between" align="center" w="100%" wrap="nowrap">
            <Text fw={600}>{label || t("policies:form.fields.policyText.label", { defaultValue: "Policy text" })}</Text>
            <Group gap="xs" wrap="nowrap">
              {formatSelect("sm")}
              <Tooltip label={t("policies:form.text.exitFullscreen", { defaultValue: "Exit full screen" })} withArrow>
                <ActionIcon variant="subtle" size="lg" onClick={close} aria-label={t("common:buttons.close", { defaultValue: "Close" })}>
                  <AppIcon iconName="Minimize" size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>
        }
        styles={{ title: { flex: 1 }, header: { alignItems: "center" } }}
      >
        <Box>{fullscreen && renderEditor(true)}</Box>
      </StackedModal>
    </>
  );
};
